import { ORDER_TYPES, PAYMENT_METHOD_TYPES } from '@exodus/fiat-client';
import type { Quote } from '@exodus/fiat-ramp/api';
import { useDebounceCallback } from '@passkeys/hooks';
import { useCallback, useEffect, useState } from 'react';

import type { Asset } from '@/types';
import useReceiveAddress from '@/ui/hooks/use-receive-address';
import exodus from '../../../exodus.js';

type UseBuyQuoteReturnValue = {
  value?: Quote;
  loading: boolean;
};

type UseBuyQuoteOptions = {
  providerName: string;
  fiatAsset: Asset;
  fiatAmount: number;
  cryptoAsset: Asset;
  paymentMethod?: string;
};

export const useBuyQuote = ({
  providerName,
  fiatAmount,
  fiatAsset,
  cryptoAsset,
  paymentMethod = PAYMENT_METHOD_TYPES['card'],
}: UseBuyQuoteOptions): UseBuyQuoteReturnValue => {
  const [loadingQuotes, setLoadingQuotes] = useState(true);
  const [fetchedQuotes, setFetchedQuotes] = useState<{
    [provider: string]: Quote;
  }>();

  const { address, loading: loadingAddress } = useReceiveAddress({
    assetName: cryptoAsset.name,
  });

  const updateQuotes = useCallback(async (options) => {
    try {
      setFetchedQuotes(await exodus.fiatRamp.getQuotes(options));
    } finally {
      setLoadingQuotes(false);
    }
  }, []);
  const debouncedUpdateQuotes = useDebounceCallback(updateQuotes, 300);

  useEffect(() => {
    if (!fiatAmount || !address) {
      return;
    }

    (async () => {
      setLoadingQuotes(true);

      try {
        await debouncedUpdateQuotes({
          fromAsset: fiatAsset.name,
          fromAmount: fiatAmount,
          paymentMethod,
          toAsset: cryptoAsset.name,
          type: ORDER_TYPES.buy,
          walletAddress: address,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  }, [
    fiatAmount,
    address,
    debouncedUpdateQuotes,
    fiatAsset,
    paymentMethod,
    cryptoAsset,
  ]);

  return {
    loading: loadingAddress || loadingQuotes,
    value: fetchedQuotes?.[providerName],
  };
};
